// src/components/outputs/blogSeo/BlogSeoCard.jsx — the main card of the Blog SEO output.
//
// Figma «Content Generator 1.1», node 828:13480 and the prototype's HTML / META
// views. A header and ONE tab panel:
//   header   SegmentedTabs Testo | HTML | META (a real tablist: roles, arrows),
//            «Segnala un errore» (⚠️ disabled — see below), «Copia» and the
//            primary «Scarica tutto» with the download catalogue
//   Testo    ArticleView of the article + FAQ + cited sources (each piece only
//            when it exists), then the word count strip
//   HTML     labelled code boxes, each with its own icon «Copia» (text only):
//            «H1 / Titolo» (<h1> of the title), «Corpo» (body_html VERBATIM,
//            open, scrolling — no «Mostra HTML» fold; data-testid="piece-source"
//            so a hand selection inside it is left to the browser: that text IS
//            markup, cleanCopy.js copies it verbatim) and «FAQ» (the FAQ piece
//            as serialize/html.js writes it); then «Internal links» as cards
//            (anchor chip + url, from publish/pieces/blogSeo.js ::
//            internalLinkEntries) — each box only when the run carries it
//   META     labelled fields with an icon «Copia» (text only) and the counter
//            of publish/piece.js :: LIMITS: «Title page» (the h1 piece), «Meta
//            title» (60), «Meta description» (160), «URL slug» (in the accent)
//
// «Copia» in the header copies the ACTIVE tab (copyPayloadFor, pure, tested):
//   Testo  text/plain + text/html of the article, the FAQ and the cited sources
//          — the clean fragment of serialize/html.js (h1/h2/h3, p, ul/ol/li,
//          strong/em, a, no style); id "all-<entryId>", so the orchestrator's
//          leave guard counts the article copied as kept (COPY_ALL_PREFIX)
//   HTML   text/plain alone: the verbatim body_html
//   META   text/plain alone: the four fields labelled (allPiecesText)
// A hand selection inside the Testo panel or a META box copies clean through
// cleanCopy.js :: onCopyWithinBox: the accent on H2, the rule of an H3 block and
// the boxes' tint are inline styles, dropped on the way.
//
// «Scarica tutto» lists downloadOptionsForEntry(entry) (publish/downloads.js:
// for the blog ready-txt / ready-html / all-txt / all-md / all-html / all-docx
// / all-pdf / brief-json — docx and pdf load their serialiser on demand) in the
// shared DownloadMenu; a row builds with buildDownload and saves with
// downloadBlob; a rejected build saves nothing and says nothing (its reason is
// a code or a chunk that did not load). This card is drawn by the generator's
// run card AND the console's run detail; the detail page hides its own
// «Scarica» for this format so the action exists once.
//
// TODO(«Segnala un errore»): the only backend write is POST
// /pipeline/run/{task_id}/review, which is an APPROVE / REJECT decision: it
// moves the run to `approved` / `rejected`, once. A report that rejects the
// run is a product decision not taken yet, so the button is drawn disabled
// with a tooltip and calls nothing.
//
//   BlogSeoCard({ entry, pieces, copiedId, onCopy, t, w, color })

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check, Download, ChevronDown, Flag, Link as LinkIcon } from 'lucide-react';
import { COLORS, FONT_MONO, FONT_TEXT, RADII, SHADOW } from '../../../constants/theme.js';
import { pieceText, pieceHtml, pieceLength } from '../../../publish/pieces/index.js';
import { internalLinkEntries } from '../../../publish/pieces/blogSeo.js';
import { downloadOptionsForEntry, buildDownload, downloadBlob, primaryText, allPiecesText } from '../../../publish/downloads.js';
import { escapeHtml, blocksToHtml } from '../../../publish/serialize/html.js';
import SegmentedTabs from '../../ui/SegmentedTabs.jsx';
import DownloadMenu from '../DownloadMenu.jsx';
import PieceCounter from '../pieces/PieceCounter.jsx';
import { MetaFooter } from '../OutputPrimitives.jsx';
import { onCopyWithinBox } from '../pieces/cleanCopy.js';
import ArticleView from './ArticleView.jsx';

const defaultW = (o) => `rgba(255,255,255,${o})`;

export const TABS = Object.freeze(['text', 'html', 'meta']);
const META_IDS = ['h1', 'meta_title', 'meta_description', 'slug'];
const META_LABEL = { h1: 'title', meta_title: 'metaTitle', meta_description: 'metaDescription', slug: 'slug' };

const byId = (pieces, id) => (Array.isArray(pieces) ? pieces.find((p) => p && p.id === id) : null) || null;

/** The pieces each tab draws, from the entry's piece list. */
export function tabPieces(pieces) {
  const list = Array.isArray(pieces) ? pieces.filter(Boolean) : [];
  return {
    article: byId(list, 'article'),
    faq: byId(list, 'faq'),
    sources: byId(list, 'sources'),
    h1: byId(list, 'h1'),
    html: byId(list, 'html'),
    meta: META_IDS.map((id) => byId(list, id)).filter(Boolean),
  };
}

/** The copy id of a tab's «Copia» for an entry ("all-<id>" for the text: the leave guard reads it). */
export const copyIdFor = (entryId, tab) => (tab === 'text' ? `all-${entryId}` : `piece-${entryId}-${tab}-tab`);

/** The <h1> markup of the title piece, '' without one. */
export const titleHtml = (h1) => (h1 ? `<h1>${escapeHtml(pieceText(h1))}</h1>` : '');

/**
 * What «Copia» puts on the clipboard for a tab: { text, html } — html null when
 * the tab is text only. The text tab keeps its "[N]" markers only when the
 * cited sources are listed (the rule of primaryText).
 */
export function copyPayloadFor(tab, pieces) {
  const { article, faq, sources, html, meta } = tabPieces(pieces);
  if (tab === 'html') return { text: html ? pieceText(html) : '', html: null };
  if (tab === 'meta') return { text: allPiecesText(meta), html: null };
  const list = Array.isArray(pieces) ? pieces.filter(Boolean) : [];
  const cites = Boolean(sources);
  const after = [faq, sources].filter(Boolean);
  const text = [
    article ? primaryText(list) : '',
    ...after.map((p) => `${p.label}\n\n${pieceText(p, { cites })}`),
  ].filter(Boolean).join('\n\n');
  const htmlOut = [
    article ? pieceHtml(article) : '',
    ...after.map((p) => `<h2>${escapeHtml(p.label)}</h2>\n${blocksToHtml(p.blocks)}`),
  ].filter(Boolean).join('\n');
  return { text, html: htmlOut || null };
}

const positiveInt = (v) => (typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null);
const nonEmptyString = (v) => (typeof v === 'string' && v.trim() ? v.trim() : '');

const headerButton = (t) => ({
  display: 'inline-flex', alignItems: 'center', gap: '8px',
  padding: '11px 20px 11px 16px', borderRadius: RADII.control,
  background: t.surface || COLORS.surface, border: `1px solid ${t.border || COLORS.border}`,
  color: t.text || COLORS.ink, fontFamily: FONT_TEXT, fontSize: '14px', fontWeight: 600, lineHeight: '20px',
  cursor: 'pointer', whiteSpace: 'nowrap',
});

/** The small square icon «Copia» of a labelled box (aria-label names the field). */
function IconCopy({ id, label, copied, onClick, t }) {
  return (
    <button
      type="button"
      id={id}
      aria-label={label}
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px',
        padding: 0, borderRadius: RADII.controlInner, border: `1px solid ${t.border || COLORS.border}`,
        background: t.surface || COLORS.surface, color: copied ? COLORS.accent : (t.textMuted || COLORS.textSecondary), cursor: 'pointer',
      }}
    >
      {copied ? <Check size={13} aria-hidden="true" /> : <Copy size={13} aria-hidden="true" />}
    </button>
  );
}

/**
 * A labelled box of the HTML and META tabs: the uppercase label, an optional
 * counter beside it, the icon «Copia» on the right, then the value in a tinted
 * field. `boxProps` lands on the field (the META boxes copy clean by hand).
 */
function LabeledBox({ label, counter = null, copyId, copied, onCopy, t, mono = false, testId, boxProps = {}, children }) {
  const { t: tr } = useTranslation('outputs');
  return (
    <div data-testid={testId} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          <span style={{ fontFamily: FONT_TEXT, fontSize: '11px', fontWeight: 600, lineHeight: '16px', letterSpacing: '0.04em', textTransform: 'uppercase', color: t.textMuted || COLORS.textSecondary }}>
            {label}
          </span>
          {counter}
        </div>
        <IconCopy id={copyId} label={tr('blogSeo.copyField', { field: label })} copied={copied} onClick={onCopy} t={t} />
      </div>
      <div
        {...boxProps}
        style={{
          padding: '12px 16px', borderRadius: RADII.control, background: t.inputBg || COLORS.field,
          color: t.text || COLORS.ink, fontFamily: mono ? FONT_MONO : FONT_TEXT, fontSize: mono ? '12px' : '13px',
          lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word', minWidth: 0,
          ...(boxProps.style || {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}

function MetaBox({ piece, entryId, copiedId, onCopy, t, label, accent }) {
  const boxRef = useRef(null);
  const id = `piece-${entryId}-${piece.id}`;
  const text = pieceText(piece);
  return (
    <LabeledBox
      testId={`blogseo-meta-${piece.id}`}
      label={label}
      counter={piece.limit ? <PieceCounter count={pieceLength(piece)} limit={piece.limit} t={t} /> : null}
      copyId={id}
      copied={copiedId === id}
      onCopy={() => onCopy?.(text, id, { html: null })}
      t={t}
      boxProps={{ ref: boxRef, onCopy: onCopyWithinBox(boxRef), style: piece.id === 'slug' ? { color: accent, fontFamily: FONT_MONO, fontSize: '12.5px' } : {} }}
    >
      {text}
    </LabeledBox>
  );
}

export default function BlogSeoCard({ entry, pieces, copiedId, onCopy, t = {}, w = defaultW, color }) {
  const { t: tr, i18n } = useTranslation('outputs');
  const language = i18n.language;
  const entryId = entry?.id ?? 'entry';
  const accent = color || COLORS.accent;
  const idPrefix = `blogseo-${entryId}`;
  const [tab, setTab] = useState('text');
  const textRef = useRef(null);
  const { article, faq, sources, h1, html, meta } = useMemo(() => tabPieces(pieces), [pieces]);
  const rawContent = entry?.results?.content;
  const content = rawContent && typeof rawContent === 'object' ? rawContent : {};
  const links = useMemo(() => internalLinkEntries(rawContent), [rawContent]);
  const wordCount = positiveInt(content.word_count);
  const readingTime = nonEmptyString(content.reading_time);

  // «Scarica tutto» — rows and labels follow the UI language, like the piece list.
  const [dlOpen, setDlOpen] = useState(false);
  const dlRef = useRef(null);
  const closeDl = useCallback(() => setDlOpen(false), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const downloadOptions = useMemo(() => downloadOptionsForEntry(entry), [entry, language]);
  const handleDownload = useCallback((id) => {
    setDlOpen(false);
    buildDownload(entry, id)
      .then(({ blob, filename }) => downloadBlob(blob, filename))
      .catch(() => {});
  }, [entry]);

  const copyId = copyIdFor(entryId, tab);
  const copied = copiedId === copyId;
  const handleCopy = () => {
    const { text, html: htmlPayload } = copyPayloadFor(tab, pieces);
    onCopy?.(text, copyId, { html: htmlPayload });
  };
  const fieldCopy = (id, text) => () => onCopy?.(text, id, { html: null });

  const tabs = TABS
    .filter((key) => key === 'text' || (key === 'html' ? Boolean(html || h1 || faq || links.length) : meta.length > 0))
    .map((key) => ({ key, label: tr(`blogSeo.tabs.${key}`) }));
  const active = tabs.some((x) => x.key === tab) ? tab : 'text';
  const faqHtml = faq ? blocksToHtml(faq.blocks) : '';
  const h1Html = titleHtml(h1);

  return (
    <section
      data-testid="blogseo-card"
      aria-label={tr('blogSeo.cardLabel')}
      style={{
        background: t.surface || COLORS.surface, border: `1px solid ${t.border || COLORS.border}`,
        borderRadius: RADII.section, boxShadow: SHADOW.card, padding: '25px 0', minWidth: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', padding: '0 24px 24px', borderBottom: `1px solid ${t.border || COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
          <SegmentedTabs tabs={tabs} value={active} onChange={setTab} label={tr('blogSeo.tabs.label')} idPrefix={idPrefix} />
          <button
            type="button"
            disabled
            title={tr('blogSeo.reportErrorUnavailable')}
            aria-describedby={`${idPrefix}-report-hint`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: 0, border: 'none', background: 'transparent', color: t.textMuted || COLORS.textTertiary, fontFamily: FONT_TEXT, fontSize: '12px', lineHeight: '16px', cursor: 'not-allowed' }}
          >
            <Flag size={16} aria-hidden="true" />
            {tr('blogSeo.reportError')}
          </button>
          <span id={`${idPrefix}-report-hint`} hidden>{tr('blogSeo.reportErrorUnavailable')}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button type="button" id={copyId} onClick={handleCopy} style={headerButton(t)} aria-live="polite">
            {copied ? <Check size={20} aria-hidden="true" /> : <Copy size={20} aria-hidden="true" />}
            {copied ? tr('common.copied') : tr('common.copy')}
          </button>
          {downloadOptions.length > 0 && (
            <div ref={dlRef} style={{ position: 'relative' }}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={dlOpen}
                onClick={() => setDlOpen((o) => !o)}
                style={{ ...headerButton(t), padding: '11px 12px 11px 16px', background: COLORS.accent, border: `1px solid ${COLORS.accent}`, color: COLORS.surface }}
              >
                <Download size={20} aria-hidden="true" />
                {tr('blogSeo.downloadAll')}
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              {dlOpen && (
                <DownloadMenu options={downloadOptions} onSelect={handleDownload} onClose={closeDl} containerRef={dlRef} t={t} />
              )}
            </div>
          )}
        </div>
      </div>

      <div role="tabpanel" id={`${idPrefix}-panel`} aria-labelledby={`${idPrefix}-${active}`} style={{ padding: '24px 24px 0' }}>
        {active === 'text' && (
          <div ref={textRef} data-testid="blogseo-text-panel" onCopy={onCopyWithinBox(textRef)}>
            {article && <ArticleView article={article} faq={faq} extras={sources ? [sources] : []} t={t} w={w} color={accent} />}
            <MetaFooter
              style={{ marginTop: '18px' }}
              items={[
                wordCount != null ? tr('common.wordCount', { count: wordCount }) : null,
                readingTime ? tr('pieces.ui.readingTime', { time: readingTime }) : null,
              ]}
            />
          </div>
        )}

        {active === 'html' && (
          <div data-testid="blogseo-html-panel" style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
            {h1Html && (
              <LabeledBox testId="blogseo-html-h1" label={tr('blogSeo.html.title')} copyId={`piece-${entryId}-html-h1`} copied={copiedId === `piece-${entryId}-html-h1`} onCopy={fieldCopy(`piece-${entryId}-html-h1`, h1Html)} t={t} mono boxProps={{ 'data-testid': 'piece-source' }}>
                {h1Html}
              </LabeledBox>
            )}
            {html && (
              <LabeledBox testId="blogseo-html-body" label={tr('blogSeo.html.body')} copyId={`piece-${entryId}-html`} copied={copiedId === `piece-${entryId}-html`} onCopy={fieldCopy(`piece-${entryId}-html`, pieceText(html))} t={t} mono boxProps={{ 'data-testid': 'piece-source', style: { maxHeight: '520px', overflow: 'auto' } }}>
                {pieceText(html)}
              </LabeledBox>
            )}
            {faqHtml && (
              <LabeledBox testId="blogseo-html-faq" label={tr('blogSeo.html.faq')} copyId={`piece-${entryId}-html-faq`} copied={copiedId === `piece-${entryId}-html-faq`} onCopy={fieldCopy(`piece-${entryId}-html-faq`, faqHtml)} t={t} mono boxProps={{ 'data-testid': 'piece-source', style: { maxHeight: '360px', overflow: 'auto' } }}>
                {faqHtml}
              </LabeledBox>
            )}
            {links.length > 0 && (
              <div data-testid="blogseo-internal-links" style={{ display: 'flex', flexDirection: 'column', gap: '10px', paddingTop: '22px', borderTop: `1px solid ${t.border || COLORS.border}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <LinkIcon size={14} color={accent} aria-hidden="true" />
                  <span style={{ fontFamily: FONT_TEXT, fontSize: '11px', fontWeight: 600, lineHeight: '16px', letterSpacing: '0.04em', textTransform: 'uppercase', color: accent }}>
                    {tr('blogSeo.html.internalLinks')}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '18px', height: '18px', padding: '0 6px', borderRadius: RADII.pill, background: COLORS.accentTint, color: accent, fontFamily: FONT_TEXT, fontSize: '11px', fontWeight: 600 }}>
                    {links.length}
                  </span>
                </div>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {links.map((l, i) => (
                    <li key={i} style={{ display: 'flex', flexDirection: 'column', gap: '6px', padding: '10px 12px', borderRadius: RADII.control, border: `1px solid ${t.border || COLORS.border}`, background: t.surface || COLORS.surface }}>
                      {l.anchor && (
                        <span style={{ alignSelf: 'flex-start', padding: '2px 10px', borderRadius: RADII.controlInner, background: COLORS.accentTint, color: accent, fontFamily: FONT_TEXT, fontSize: '12px', fontWeight: 600 }}>
                          {l.anchor}
                        </span>
                      )}
                      {l.url && <span style={{ fontFamily: FONT_TEXT, fontSize: '12px', color: t.textMuted || COLORS.textTertiary, wordBreak: 'break-all' }}>{l.url}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {active === 'meta' && (
          <div data-testid="blogseo-meta-panel" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {meta.map((piece) => (
              <MetaBox key={piece.id} piece={piece} entryId={entryId} copiedId={copiedId} onCopy={onCopy} t={t} accent={accent} label={tr(`blogSeo.meta.${META_LABEL[piece.id]}`)} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
